/**
 * Type-safe environment variable access
 * Provides runtime validation and sensible defaults
 */

/**
 * Get API base URL from environment variables
 * Falls back to localhost:3000 in development
 */
type RuntimeEnvOptions = {
  apiUrl?: string;
  hostname?: string;
};

const getRuntimeHostname = (): string | undefined => {
  if (typeof window === 'undefined') return undefined;
  return window.location.hostname;
};

export const resolveApiUrl = ({ apiUrl, hostname }: RuntimeEnvOptions = {}): string => {
  const configuredApiUrl = apiUrl ?? import.meta.env.VITE_API_URL;

  if (configuredApiUrl) {
    return configuredApiUrl;
  }

  // Runtime fallback based on hostname — works regardless of build-time env vars
  if (hostname === 'app.mensova.com') {
    return 'https://api.mensova.com/v1';
  }
  if (hostname === 'app.skillspec.ai') {
    return 'https://api.skillspec.ai/v1';
  }
  if (hostname === 'staging.mensova.com') {
    return 'https://staging-api.mensova.com/v1';
  }
  if (hostname === 'staging.skillspec.ai') {
    return 'https://staging-api.skillspec.ai/v1';
  }

  return 'http://localhost:3000/v1';
};

export const getApiUrl = (): string => resolveApiUrl({ hostname: getRuntimeHostname() });

/**
 * Check if analytics is enabled
 */
export const isAnalyticsEnabled = (): boolean => {
  return import.meta.env.VITE_ENABLE_ANALYTICS === 'true';
};

/**
 * Check if debug mode is enabled
 */
export const isDebugMode = (): boolean => {
  return import.meta.env.VITE_DEBUG_MODE === 'true' || import.meta.env.DEV === true;
};

/**
 * Check if running in production
 */
export const isProduction = (): boolean => {
  return import.meta.env.PROD === true;
};

/**
 * Check if running in development
 */
export const isDevelopment = (): boolean => {
  return import.meta.env.DEV === true;
};

/**
 * Check if the app is running on a local development host.
 * Returns true for localhost and 127.0.0.1; false for all other hostnames.
 */
export const isLocalHostname = (hostname: string | undefined): boolean => {
  if (hostname === undefined) return true; // SSR / test — treat as local
  return hostname === 'localhost' || hostname === '127.0.0.1';
};

export const isLocalEnvironment = (): boolean => {
  return isLocalHostname(getRuntimeHostname());
};

/**
 * Get all environment variables (for debugging)
 * Only use in development
 */
export const getEnv = (): ImportMetaEnv => {
  if (!isDevelopment()) {
    console.warn('getEnv() should only be used in development');
  }
  return import.meta.env;
};

/**
 * Validate required environment variables on app startup
 * Throws error if required variables are missing
 */
export const validateEnv = (options: RuntimeEnvOptions = {}): void => {
  const hostname = options.hostname ?? getRuntimeHostname();
  const apiUrl = resolveApiUrl({
    apiUrl: options.apiUrl,
    hostname,
  });

  if (!apiUrl) {
    throw new Error('VITE_API_URL is required but not defined');
  }

  // Fail-fast: on deployed (non-localhost) hosts, the API URL must NOT be a
  // localhost address. This catches forgotten VITE_API_URL on staging/custom domains.
  if (!isLocalHostname(hostname)) {
    const isLocalhostUrl = /^https?:\/\/(localhost|127\.0\.0\.1)(:|\/|$)/.test(apiUrl);
    if (isLocalhostUrl) {
      throw new Error(
        `VITE_API_URL is not configured for this deployment. ` +
          `The app is running on "${hostname}" but the resolved API URL ` +
          `is "${apiUrl}" (localhost). Set the VITE_API_URL environment variable ` +
          `to the correct API endpoint for this environment.`
      );
    }
  }

  if (isDebugMode()) {
    console.log('[ENV] Environment validated successfully');
    console.log('[ENV] API URL:', apiUrl);
    console.log('[ENV] Analytics:', isAnalyticsEnabled());
    console.log('[ENV] Production:', isProduction());
  }
};
